import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppNav from './AppNav';
import Button from './Button';
import Icon from './icons/Icon';
import StockMenu from './StockMenu';
import StockList from './StockList';
import DrawerStockDetail from './DrawerStockDetail';
import { useStocks } from '../hooks/useStocks';
import type { StockEntity } from '../types/data';
// Reuse App.css layout classes (app/app-main/content-area/content-nav/content-main)
import './PageStock.css';

function PageStock() {
  const { viewMode: rawViewMode, groupId: rawGroupId, legacyGroupId } = useParams<{
    viewMode?: string;
    groupId?: string;
    legacyGroupId?: string;
  }>();
  const navigate = useNavigate();
  const {
    activeStocks,
    activeStockCategories,
    activeStockPlaces,
    createStock,
    updateStock,
    deleteStock,
    createStockCategory,
    createStockPlace,
    reorderStockCategories,
    reorderStockPlaces,
    reorderStocks,
  } = useStocks();
  const [selectedStockId, setSelectedStockId] = useState<string | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [draftStock, setDraftStock] = useState<StockEntity | null>(null);

  const effectiveViewMode: 'category' | 'place' = rawViewMode === 'place' ? 'place' : 'category';
  const effectiveGroupParam = rawGroupId ?? legacyGroupId;
  const emptyGroupSlug = effectiveViewMode === 'place' ? 'unset' : 'uncategorized';

  useEffect(() => {
    if (legacyGroupId) {
      navigate(`/stock/category/${legacyGroupId}/`, { replace: true });
      return;
    }
    if (rawViewMode && rawViewMode !== 'category' && rawViewMode !== 'place') {
      navigate('/stock/category/uncategorized/', { replace: true });
      return;
    }
    if (!rawGroupId) {
      navigate(`/stock/${effectiveViewMode}/${emptyGroupSlug}/`, { replace: true });
      return;
    }
    if (effectiveViewMode === 'place') {
      if (rawGroupId === 'unset') return;
      const exists = activeStockPlaces.some(place => place.id === rawGroupId);
      if (!exists) {
        navigate('/stock/place/unset/', { replace: true });
        return;
      }
      return;
    }
    if (rawGroupId !== 'uncategorized') {
      const exists = activeStockCategories.some(c => c.id === rawGroupId);
      if (!exists) {
        navigate('/stock/category/uncategorized/', { replace: true });
      }
    }
  }, [activeStockCategories, activeStockPlaces, effectiveViewMode, emptyGroupSlug, legacyGroupId, navigate, rawGroupId]);

  const selectedGroupId: string | null = useMemo(() => {
    if (!effectiveGroupParam) return null;
    if (effectiveViewMode === 'place') {
      return effectiveGroupParam === 'unset' ? null : effectiveGroupParam;
    }
    return effectiveGroupParam === 'uncategorized' ? null : effectiveGroupParam;
  }, [effectiveGroupParam, effectiveViewMode]);

  const filteredStocks = useMemo(() => {
    if (effectiveViewMode === 'place') {
      if (selectedGroupId === null) {
        return activeStocks.filter(s => !s.stock_place_id);
      }
      return activeStocks.filter(s => s.stock_place_id === selectedGroupId);
    }
    if (selectedGroupId === null) {
      return activeStocks.filter(s => !s.stock_category_id);
    }
    return activeStocks.filter(s => s.stock_category_id === selectedGroupId);
  }, [activeStocks, effectiveViewMode, selectedGroupId]);

  const currentIndex = useMemo(() => {
    if (!selectedStockId) return -1;
    return filteredStocks.findIndex(s => s.id === selectedStockId);
  }, [filteredStocks, selectedStockId]);

  const handleSelectGroup = (groupId: string | null) => {
    const fallback = effectiveViewMode === 'place' ? 'unset' : 'uncategorized';
    if (groupId === null) {
      navigate(`/stock/${effectiveViewMode}/${fallback}/`);
    } else {
      navigate(`/stock/${effectiveViewMode}/${groupId}/`);
    }
  };

  const handleChangeViewMode = (nextViewMode: 'category' | 'place') => {
    const fallback = nextViewMode === 'place' ? 'unset' : 'uncategorized';
    navigate(`/stock/${nextViewMode}/${fallback}/`);
  };

  const handleCreateStock = () => {
    const now = new Date().toISOString();
    const newStock: StockEntity = {
      id: '',
      title: '',
      body: '',
      where_to_get: undefined,
      count: 0,
      created_at: now,
      modified_at: now,
      is_deleted: false,
      stock_category_id: effectiveViewMode === 'category' ? (selectedGroupId ?? undefined) : undefined,
      stock_place_id: effectiveViewMode === 'place' ? (selectedGroupId ?? undefined) : undefined,
    };
    setDraftStock(newStock);
    setSelectedStockId(null);
    setDrawerOpen(true);
  };

  return (
    <div className="app">
      <AppNav activeItem="stock" />
      <div className="app-main">
        <div className="content-area">
          {/* 左: メニュー */}
          <div className="content-nav">
            <div className="stock-nav">
              <div className="text-heading-large">ストック</div>
              <div className="stock-menu-tabs">
                <button
                  type="button"
                  className={`stock-menu-tab ${effectiveViewMode === 'category' ? 'active' : ''}`}
                  onClick={() => handleChangeViewMode('category')}
                  aria-label="カテゴリー"
                >
                  <Icon name="folder" size={24} />
                </button>
                <button
                  type="button"
                  className={`stock-menu-tab ${effectiveViewMode === 'place' ? 'active' : ''}`}
                  onClick={() => handleChangeViewMode('place')}
                  aria-label="場所"
                >
                  <Icon name="cabinet" size={24} />
                </button>
              </div>
              <StockMenu
                categories={activeStockCategories}
                places={activeStockPlaces}
                stocks={activeStocks}
                viewMode={effectiveViewMode}
                selectedGroupId={selectedGroupId}
                onSelect={handleSelectGroup}
                onReorderCategories={async (sourceId, targetId, insertAfter) => {
                  await reorderStockCategories(sourceId, targetId, insertAfter);
                }}
                onReorderPlaces={async (sourceId, targetId, insertAfter) => {
                  await reorderStockPlaces(sourceId, targetId, insertAfter);
                }}
              />
            </div>
          </div>
          {/* 右: メイン */}
          <div className="content-main">
            <div className="stock-panel">
              <div className="stock-panel-header">
                <div className="stock-panel-header-content">
                  <div className="stock-panel-title text-heading-large">
                    {effectiveViewMode === 'place'
                      ? (selectedGroupId === null ? '未設定' : (activeStockPlaces.find(place => place.id === selectedGroupId)?.title ?? ''))
                      : (selectedGroupId === null ? '未分類' : (activeStockCategories.find(category => category.id === selectedGroupId)?.title ?? ''))}
                  </div>
                  <Button leftIcon="add" onClick={handleCreateStock}>ストック追加</Button>
                </div>
              </div>
              <StockList 
                stocks={activeStocks}
                stockPlaces={activeStockPlaces}
                viewMode={effectiveViewMode}
                selectedGroupId={selectedGroupId}
                onChangeCount={(stockId, newCount) => updateStock(stockId, { count: newCount })}
                onDeleteStock={(stockId) => deleteStock(stockId)}
                onSelectStock={(stockId) => { setSelectedStockId(stockId); setDrawerOpen(true); }}
                onReorderStocks={async (sourceId, targetId, insertAfter) => {
                  await reorderStocks(sourceId, targetId, insertAfter, effectiveViewMode, selectedGroupId);
                }}
              />
            </div>
          </div>
      </div>
    </div>
      <DrawerStockDetail
        stock={draftStock ?? (selectedStockId ? activeStocks.find(s => s.id === selectedStockId) : undefined)}
        isOpen={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setDraftStock(null); }}
        onSave={(s) => updateStock(s.id, {
          title: s.title,
          body: s.body,
          where_to_get: s.where_to_get,
          count: s.count,
          stock_category_id: s.stock_category_id,
          stock_place_id: s.stock_place_id,
        })}
        onCreate={async (input) => {
          const created = await createStock({
            title: input.title,
            body: input.body,
            where_to_get: input.where_to_get,
            count: input.count ?? 0,
            stock_category_id: input.stock_category_id,
            stock_place_id: input.stock_place_id,
          });
          setDraftStock(null);
          setSelectedStockId(created.id);
        }}
        onDelete={(id) => { deleteStock(id); setDrawerOpen(false); setDraftStock(null); }}
        onPreviousStock={() => {
          if (currentIndex > 0) {
            setSelectedStockId(filteredStocks[currentIndex - 1].id);
          }
        }}
        onNextStock={() => {
          if (currentIndex >= 0 && currentIndex < filteredStocks.length - 1) {
            setSelectedStockId(filteredStocks[currentIndex + 1].id);
          }
        }}
        hasPreviousStock={currentIndex > 0}
        hasNextStock={currentIndex >= 0 && currentIndex < filteredStocks.length - 1}
        activeStockCategories={activeStockCategories}
        activeStockPlaces={activeStockPlaces}
        createStockCategory={createStockCategory}
        createStockPlace={createStockPlace}
      />
    </div>
  );
}

export default PageStock;
